/**
 * DEMO-ONLY Vietnamese ⇄ English toggle for Meta App Review screencasts.
 *
 * WHY: Meta requires the app UI shown in review videos to be in English. This adds a
 * floating toggle that renders the existing screens in English WITHOUT rewriting any
 * component: when English is on it translates known Vietnamese UI text in the live
 * DOM using the VI→EN dictionary, and keeps translating nodes React renders afterwards
 * via a MutationObserver. Toggling back restores every original string. The choice is
 * remembered in localStorage.
 *
 * SAFETY / SCOPE:
 *  - ADD-ONLY: mounted once next to <App/> in main.tsx; touches no existing component.
 *  - Default is Vietnamese → the production experience is unchanged until someone
 *    clicks the toggle.
 *  - Translates whole exact text nodes + the label attributes placeholder/title/
 *    aria-label (incl. on <input>/<select>). It NEVER translates <input>/<textarea>
 *    VALUES (user-typed content) and touches no application logic or network calls.
 */
import { useEffect, useState } from 'react';
import { VI_EN } from './dictionary';

type Lang = 'vi' | 'en';

const STORAGE_KEY = 'ads360_demo_lang';
const TOGGLE_ID = 'demo-lang-toggle';
// Subtrees translated for attributes but never descended into for text, or skipped
// entirely. We descend into <select> (to translate <option> text) and <input> (no
// text children), translate their label attributes, but never a <textarea>'s text.
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE']);
const ATTRS = ['placeholder', 'title', 'aria-label'];

// Normalise for matching only: unify "…"/"...", collapse whitespace. The English
// value is used verbatim; only the lookup key is normalised so near-miss source
// strings (ellipsis char vs three dots, double spaces) still translate.
function norm(s: string): string {
  return s.replace(/…/g, '...').replace(/\s+/g, ' ').trim();
}
const VI_EN_NORM: Record<string, string> = {};
for (const k of Object.keys(VI_EN)) {
  const v = VI_EN[k];
  if (v !== undefined) VI_EN_NORM[norm(k)] = v;
}
function look(s: string): string | undefined {
  return VI_EN_NORM[norm(s)];
}

// Module-scope state (this component is a singleton mounted once).
const textOrig = new Map<Text, string>();
const attrOrig = new Map<Element, Record<string, string>>();
let currentLang: Lang = 'vi';

function translateText(node: Text): void {
  const raw = node.nodeValue ?? '';
  const trimmed = raw.trim();
  if (!trimmed) return;
  const en = look(trimmed);
  if (en === undefined) return;
  if (!textOrig.has(node)) textOrig.set(node, raw);
  const leadLen = raw.length - raw.trimStart().length;
  const trailLen = raw.length - raw.trimEnd().length;
  const lead = raw.slice(0, leadLen);
  const trail = trailLen ? raw.slice(raw.length - trailLen) : '';
  node.nodeValue = lead + en + trail;
}

function translateAttrs(el: Element): void {
  for (const attr of ATTRS) {
    const v = el.getAttribute(attr);
    if (!v) continue;
    const en = look(v.trim());
    if (en === undefined) continue;
    let saved = attrOrig.get(el);
    if (!saved) {
      saved = {};
      attrOrig.set(el, saved);
    }
    if (!(attr in saved)) saved[attr] = v;
    el.setAttribute(attr, en);
  }
}

function walk(node: Node): void {
  if (node.nodeType === Node.TEXT_NODE) {
    translateText(node as Text);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as Element;
  if (
    el.id === TOGGLE_ID ||
    SKIP_TAGS.has(el.tagName) ||
    (el as HTMLElement).isContentEditable === true ||
    !!el.closest?.(`#${TOGGLE_ID}`)
  ) {
    return;
  }
  translateAttrs(el);
  // Translate a <textarea>'s label attributes but never its text (user content).
  if (el.tagName === 'TEXTAREA') return;
  for (const child of Array.from(el.childNodes)) walk(child);
}

function applyAll(): void {
  if (document.body) walk(document.body);
}

function restoreAll(): void {
  textOrig.forEach((orig, node) => {
    try {
      node.nodeValue = orig;
    } catch {
      /* node may be detached */
    }
  });
  textOrig.clear();
  attrOrig.forEach((attrs, el) => {
    for (const k of Object.keys(attrs)) {
      try {
        el.setAttribute(k, attrs[k]);
      } catch {
        /* element may be detached */
      }
    }
  });
  attrOrig.clear();
}

function readLang(): Lang {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'en' ? 'en' : 'vi';
  } catch {
    return 'vi';
  }
}

export default function DemoLang() {
  const [lang, setLang] = useState<Lang>(readLang);

  useEffect(() => {
    currentLang = lang;
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore private-mode storage errors */
    }
    if (lang === 'en') {
      applyAll();
      const t1 = window.setTimeout(applyAll, 60);
      const t2 = window.setTimeout(applyAll, 400);
      return () => {
        window.clearTimeout(t1);
        window.clearTimeout(t2);
      };
    }
    restoreAll();
    return undefined;
  }, [lang]);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      if (currentLang !== 'en') return;
      for (const m of mutations) {
        if (m.type === 'characterData' && m.target.nodeType === Node.TEXT_NODE) {
          translateText(m.target as Text);
        } else {
          m.addedNodes.forEach((n) => walk(n));
        }
      }
    });
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    }
    return () => observer.disconnect();
  }, []);

  const btnLabel = lang === 'vi' ? '🌐 English' : '🌐 Tiếng Việt';

  return (
    <div
      id={TOGGLE_ID}
      style={{
        position: 'fixed',
        right: '16px',
        bottom: '16px',
        zIndex: 2147483647,
        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
      }}
    >
      <button
        type="button"
        onClick={() => setLang(lang === 'vi' ? 'en' : 'vi')}
        title="Switch the interface language (demo)"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 14px',
          borderRadius: '9999px',
          border: '1px solid rgba(0,0,0,0.12)',
          background: '#111827',
          color: '#ffffff',
          fontSize: '13px',
          fontWeight: 600,
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(0,0,0,0.22)',
        }}
      >
        {btnLabel}
      </button>
    </div>
  );
}
